import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useJsonLd from '../seo/useJsonLd';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import {
  IconMap2,
  IconBolt,
  IconBrandStrava,
  IconVideo,
  IconWorld,
  IconPhoto,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconBrandDiscord,
  IconBrandReddit,
  IconDownload,
  IconRoute,
  IconMail,
} from '@tabler/icons-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { getLogoForStyle } from '../utils/styleLogos';
import TagFilter from '../components/TagFilter';
import { groupTemplatesByStyle } from '../components/StylePicker';
import StyleAhaCard from '../components/StyleAhaCard';
import Footer from '../components/Footer';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import sakurasPreview from '../assets/images/sakuras_maps_example.png';
import './ChallengePage.css';
import './MapGenerationPage.css';
const marketingSeo = require('../seo/marketingSeo');

// Hero video: four transition clips played back-to-back, looping. Each one
// shows the Strava map morphing into a different Mythic Map style.
const MAPS_LANDING_ASSET = (file) =>
  `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fmaps_landing%2F${file}?alt=media`;

const HERO_VIDEOS = [
  MAPS_LANDING_ASSET('MeadowWalkTransition.mp4'),
  MAPS_LANDING_ASSET('PocketAdventureTransition.mp4'),
  MAPS_LANDING_ASSET('StileLiberteGiroTransition.mp4'),
  MAPS_LANDING_ASSET('TibetanThangkaTransition.mp4'),
];

// Bridge frame shown between video transitions: the "Strava map" still.
// Thematically every transition starts here, so it never looks out of place.
const HERO_POSTER =
  'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fonboarding_modal%2FOriginalMap3D.png?alt=media';

// Thumbnail strip for the "see what the community made" Art feed cross-link.
// Reuses the same onboarding example maps shown elsewhere as social proof.
const ONBOARDING_ASSET = (file) =>
  `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fonboarding_modal%2F${file}?alt=media`;

const FEED_THUMBS = [
  ONBOARDING_ASSET('Japanese3D.png'),
  ONBOARDING_ASSET('StardustBrutalism3D.png'),
  ONBOARDING_ASSET('PocketAdventure3D.png'),
  ONBOARDING_ASSET('MeadowWalk3D.png'),
];

const MapGenerationPage = ({ user, onLoginRequired }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const landingViewedRef = useRef(false);

  const [styleGroups, setStyleGroups] = useState([]);
  const [isLoadingStyles, setIsLoadingStyles] = useState(true);
  const [stylesError, setStylesError] = useState(null);
  const [tagCategories, setTagCategories] = useState({});
  const [activeTagCategory, setActiveTagCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [use3D, setUse3D] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [heroVideoIndex, setHeroVideoIndex] = useState(0);
  const heroVideoRef = useRef(null);

  // When the index advances, swap the <video> source on the same DOM element
  // (instead of remounting with key=) so we don't flash the poster between
  // clips. .load() picks up the new src, then we manually .play().
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.load();
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [heroVideoIndex]);

  useEffect(() => {
    if (landingViewedRef.current) return;
    landingViewedRef.current = true;
    posthog?.capture('map_gen_landing_viewed', {
      page: 'map_generation_landing',
      is_authenticated: !!user,
    });
  }, [posthog, user]);

  // Load prompt templates and group by base style name.
  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      setIsLoadingStyles(true);
      setStylesError(null);
      try {
        const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
        const result = await getTemplatesFunc();
        if (cancelled) return;
        const list = Array.isArray(result.data?.templates)
          ? result.data.templates.filter((tpl) => tpl.enabled !== false && !tpl.adminOnly)
          : [];
        // groupTemplatesByStyle keeps per-variant example URLs (2D vs 3D)
        // so the 2D/3D toggle below can swap which one we render.
        setStyleGroups(groupTemplatesByStyle(list));
      } catch (err) {
        if (cancelled) return;
        console.error('[MapGenerationPage] failed to load templates:', err);
        setStylesError(t('mapGenPage.stylesLoadError', 'Unable to load styles right now.'));
      } finally {
        if (!cancelled) setIsLoadingStyles(false);
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Load category → tags mapping from settings/styleTags
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'styleTags'));
        if (cancelled) return;
        if (snap.exists() && snap.data().categories) {
          setTagCategories(snap.data().categories);
        }
      } catch (err) {
        console.warn('[MapGenerationPage] could not load styleTags:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Default the active tag category to the first one loaded (e.g. "Type") so
  // TagFilter renders chips immediately.
  useEffect(() => {
    if (!activeTagCategory) {
      const first = Object.keys(tagCategories || {})[0];
      if (first) setActiveTagCategory(first);
    }
  }, [tagCategories, activeTagCategory]);

  const visibleGroups = useMemo(() => {
    if (!activeTag) return styleGroups;
    return styleGroups.filter((g) => Array.isArray(g.tags) && g.tags.includes(activeTag));
  }, [styleGroups, activeTag]);

  const handleStartCreate = (groupName) => {
    const dim = use3D ? '3d' : '2d';
    posthog?.capture('map_gen_landing_cta_clicked', {
      page: 'map_generation_landing',
      destination: 'create',
      style: groupName || null,
      dimension: dim,
    });
    if (!user && onLoginRequired) {
      onLoginRequired();
      return;
    }
    navigate(groupName ? `/create?style=${encodeURIComponent(groupName)}&dim=${dim}` : '/create');
  };

  const handleStravaCta = () => {
    posthog?.capture('map_gen_landing_strava_clicked', {
      page: 'map_generation_landing',
    });
    if (!user && onLoginRequired) {
      onLoginRequired();
      return;
    }
    navigate('/create');
  };

  // Sends visitors (especially logged-out ones) to the public Art feed so they
  // can see what others have made. The feed is public, so no login gate.
  const handleArtFeedCta = () => {
    posthog?.capture('map_gen_artfeed_cta_clicked', {
      page: 'map_generation_landing',
      is_authenticated: !!user,
    });
    navigate('/art');
  };

  const faqs = marketingSeo.MAPS_FAQS.map((f) => ({ q: t(f.qKey, f.q), a: t(f.aKey, f.a) }));

  // JSON-LD + title (shared source; only injects if the static HTML lacks it).
  useJsonLd('/maps');

  return (
    <div className="challenge-page map-gen-page">
      <div className="challenge-container">

        {/* Header */}
        <div className="challenge-header-section">
          <div className="challenge-header">
            <h1>
              <IconMap2 size={32} style={{ color: '#c4956a', marginRight: 10, flexShrink: 0 }} />
              {t('mapGenPage.title', 'Turn your Strava into illustrated maps')}
            </h1>
            <p className="challenge-subtitle">
              {t('mapGenPage.subtitle', 'Your everyday activities, turned into art. Automatically. In 40+ styles.')}
            </p>
          </div>
        </div>

        {/* Personalised "see your activity as art" aha card. Branches internally:
            logged-out -> mock-up, returning user -> latest map, new user -> real generation. */}
        <div className="section-dark style-aha-section">
          <div className="section-inner">
            <StyleAhaCard user={user} onStravaConnect={handleStravaCta} styleGroups={styleGroups} />
          </div>
        </div>

        {/* Hero */}
        <div className="section-dark map-gen-hero-section">
          <div className="section-inner">
            <div className="map-gen-hero">
              <div className="map-gen-hero-video-wrap">
                {/* Plays the 4 transition clips sequentially, then loops.
                   Same <video> element across clips: the effect above swaps
                   the src and triggers play(), so there's no poster flash. */}
                <video
                  ref={heroVideoRef}
                  src={HERO_VIDEOS[heroVideoIndex]}
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                  poster={HERO_POSTER}
                  className="map-gen-hero-video"
                  onEnded={() => setHeroVideoIndex((i) => (i + 1) % HERO_VIDEOS.length)}
                />
              </div>
              <div className="map-gen-hero-text">
                <h2>{t('mapGenPage.heroHeadline', 'You finish the activity. The art finishes itself.')}</h2>
                <p>{t('mapGenPage.heroBody', 'Connect Strava once. Every activity becomes a stylised map, with a 10-second cinematic video from your Strava map into the Mythic Map, uploaded back to your activity automatically.')}</p>
                {user ? (
                  <button
                    type="button"
                    className="challenge-cta-button"
                    onClick={handleStravaCta}
                  >
                    {t('mapGenPage.ctaCreate', 'Create Map')}
                    <IconChevronRight size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="map-gen-strava-cta"
                    onClick={handleStravaCta}
                  >
                    <img src={stravaConnectButton} alt={t('mapGenPage.ctaStrava', 'Connect with Strava, it’s free')} />
                  </button>
                )}
                {!user && (
                  <p className="map-gen-strava-freenote">
                    {t('mapGenPage.heroFreeNote', 'Free to start. Don’t like your first map? Disconnect in two clicks.')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* See what others have made -> Art feed. Cross-links the public gallery
            so newcomers (especially logged-out) can browse community art for
            inspiration before making their own. */}
        <div className="section-darker">
          <div className="section-inner">
            <div className="map-gen-feed-card">
              <div className="map-gen-feed-thumbs" aria-hidden="true">
                {FEED_THUMBS.map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    loading="lazy"
                    className="map-gen-feed-thumb"
                  />
                ))}
              </div>
              <div className="map-gen-feed-text">
                <span className="map-gen-feed-eyebrow">
                  <IconPhoto size={16} />
                  {t('mapGenPage.feedEyebrow', 'Art feed')}
                </span>
                <h2>{t('mapGenPage.feedTitle', 'See what the community is making')}</h2>
                <p>
                  {t('mapGenPage.feedDesc', 'Every style here turns real Strava activities into art. Browse the Art feed to see what other adventurers have created, then make your own.')}
                </p>
                <button
                  type="button"
                  className="map-gen-feed-cta challenge-cta-button"
                  onClick={handleArtFeedCta}
                >
                  {t('mapGenPage.feedCta', 'Explore the Art feed')}
                  <IconChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* What you get */}
        <div className="section-darker">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('mapGenPage.whatYouGet', 'What you get')}</h2>
                <p className="section-description">
                  {t('mapGenPage.whatYouGetDesc', 'A map for every activity, in your favourite art style. Without lifting a finger.')}
                </p>
              </div>

              <div className="map-gen-features">
                <div className="map-gen-feature">
                  <span className="map-gen-feature-icon"><IconBolt size={20} /></span>
                  <h3>{t('mapGenPage.featureAutoGenTitle', 'Auto-generation')}</h3>
                  <p>{t('mapGenPage.featureAutoGenDesc', 'Turn it on once. Every new Strava activity becomes a Mythic Map, with zero clicks.')}</p>
                </div>
                <div className="map-gen-feature">
                  <span className="map-gen-feature-icon"><IconVideo size={20} /></span>
                  <h3>{t('mapGenPage.featureVideoTitle', '10-second transition videos')}</h3>
                  <p>{t('mapGenPage.featureVideoDesc', 'Your Strava map morphing into the Mythic Map. 2D or 3D, portrait or landscape, ready to share.')}</p>
                </div>
                <div className="map-gen-feature">
                  <span className="map-gen-feature-icon"><IconBrandStrava size={20} /></span>
                  <h3>{t('mapGenPage.featureUploadTitle', 'Lands on your Strava')}</h3>
                  <p>{t('mapGenPage.featureUploadDesc', 'We replace the cover photo of your activity. We never touch the activity itself.')}</p>
                </div>
                <div className="map-gen-feature">
                  <span className="map-gen-feature-icon"><IconWorld size={20} /></span>
                  <h3>{t('mapGenPage.featureStylesTitle', '40+ trained art styles')}</h3>
                  <p>{t('mapGenPage.featureStylesDesc', 'Edo Japan, the Andes, neon Miami, pixel-art RPG. 2D and 3D variants. New styles roughly monthly.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Style gallery */}
        <div className="section-dark" id="style-gallery">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('mapGenPage.galleryTitle', 'The style library')}</h2>
                <p className="section-description">
                  {t('mapGenPage.galleryDesc', 'See a style you like? Tap it and make a map from your last activity.')}
                </p>
              </div>

              {Object.keys(tagCategories).length > 0 && !isLoadingStyles && !stylesError && (
                <div className="map-gen-filter-wrap">
                  <TagFilter
                    availableTagCategories={tagCategories}
                    activeTagCategory={activeTagCategory}
                    activeTag={activeTag}
                    onChangeTagCategory={(cat) => {
                      setActiveTagCategory(cat);
                      setActiveTag(null);
                    }}
                    onChangeTag={setActiveTag}
                  />
                  <div className="map-gen-dimension-toggle" role="tablist" aria-label="2D or 3D">
                    <button
                      type="button"
                      className={`map-gen-dim-btn ${!use3D ? 'active' : ''}`}
                      onClick={() => setUse3D(false)}
                    >
                      2D
                    </button>
                    <button
                      type="button"
                      className={`map-gen-dim-btn ${use3D ? 'active' : ''}`}
                      onClick={() => setUse3D(true)}
                    >
                      3D
                    </button>
                  </div>
                </div>
              )}

              {isLoadingStyles ? (
                <div className="map-gen-styles-loading">
                  <div className="loading-spinner" style={{
                    width: 40, height: 40,
                    border: '3px solid var(--border-primary)',
                    borderTop: '3px solid var(--accent-gold, #c4956a)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p>{t('mapGenPage.loadingStyles', 'Loading styles…')}</p>
                </div>
              ) : stylesError ? (
                <div className="map-gen-styles-error">{stylesError}</div>
              ) : visibleGroups.length === 0 ? (
                <div className="map-gen-styles-empty">{t('mapGenPage.noStyles', 'No styles in this category yet.')}</div>
              ) : (
                <div className="map-gen-style-grid">
                  {visibleGroups.map((group) => {
                    const logo = getLogoForStyle(group.name, group.logoUrl);
                    // Prefer the variant matching the toggle, fall back to the
                    // other variant if the requested one is missing, then to
                    // any generic example URL.
                    const variantUrl = use3D
                      ? (group.exampleImageUrl3D || group.exampleImageUrl2D || group.exampleImageUrl)
                      : (group.exampleImageUrl2D || group.exampleImageUrl3D || group.exampleImageUrl);
                    return (
                      <button
                        key={group.name}
                        type="button"
                        className="map-gen-style-card"
                        onClick={() => handleStartCreate(group.name)}
                      >
                        <div className="map-gen-style-card-media">
                          {variantUrl ? (
                            <img
                              src={variantUrl}
                              alt={group.name}
                              loading="lazy"
                              className="map-gen-style-card-img"
                            />
                          ) : (
                            <div className="map-gen-style-card-placeholder">
                              <IconRoute size={36} />
                            </div>
                          )}
                          {logo && (
                            <img src={logo} alt="" className="map-gen-style-card-logo" />
                          )}
                          {group.has2D && group.has3D && (
                            <span className="map-gen-style-card-pill">2D + 3D</span>
                          )}
                        </div>
                        <div className="map-gen-style-card-body">
                          <span className="map-gen-style-card-name">{group.name}</span>
                          {group.tags.length > 0 && (
                            <span className="map-gen-style-card-tag">{group.tags[0]}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="section-gradient">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('mapGenPage.howItWorks', 'How it works')}</h2>
              </div>
              <div className="hiw-cards">
                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-connect">
                    <img src={stravaConnectButton} alt="Connect with Strava" className="hiw-strava-btn" />
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">1</div>
                    <h3>{t('mapGenPage.hiw1Title', 'Connect Strava')}</h3>
                    <p>{t('mapGenPage.hiw1Desc', 'One tap. We never touch your activity data beyond reading the route.')}</p>
                  </div>
                </div>

                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-auto">
                    <div className="hiw-auto-mock">
                      <div className="hiw-auto-mock-label">
                        <IconBolt size={16} />
                        <span>{t('mapGenPage.hiw2MockLabel', 'Auto-generation')}</span>
                      </div>
                      <div className="hiw-auto-mock-toggle">
                        <span className="hiw-auto-mock-toggle-on">ON</span>
                        <span className="hiw-auto-mock-switch" aria-hidden="true">
                          <span className="hiw-auto-mock-knob" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">2</div>
                    <h3>{t('mapGenPage.hiw2Title', 'Turn on auto-generation')}</h3>
                    <p>{t('mapGenPage.hiw2Desc', 'Recommended. From this moment on, every Strava activity becomes a Mythic Map.')}</p>
                  </div>
                </div>

                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-strava">
                    <div className="hiw-strava-mock">
                      <img
                        src={sakurasPreview}
                        alt=""
                        className="hiw-strava-mock-photo"
                      />
                      <div className="hiw-strava-mock-body">
                        <div className="hiw-strava-mock-row">
                          <div className="hiw-strava-mock-avatar" aria-hidden="true" />
                          <div className="hiw-strava-mock-meta">
                            <span className="hiw-strava-mock-name">{t('mapGenPage.hiw3MockUser', 'You')}</span>
                            <span className="hiw-strava-mock-time">{t('mapGenPage.hiw3MockTime', 'Morning ride · just now')}</span>
                          </div>
                          <IconBrandStrava size={18} className="hiw-strava-mock-logo" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">3</div>
                    <h3>{t('mapGenPage.hiw3Title', 'The map lands on your activity')}</h3>
                    <p>{t('mapGenPage.hiw3Desc', 'Auto-upload replaces your Strava cover photo with the artwork, plus the transition video from your Strava map into the Mythic Map.')}</p>
                  </div>
                </div>

                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-rewards">
                    <div className="hiw-reward-mock">
                      <div className="hiw-reward-icon">
                        <IconDownload size={24} color="#c4956a" />
                      </div>
                      <div className="hiw-reward-text">
                        <span className="hiw-reward-title">{t('mapGenPage.hiw4Mock', 'Saved to your devices')}</span>
                        <span className="hiw-reward-subtitle">{t('mapGenPage.hiw4MockSub', 'Profile, Strava, camera roll')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">4</div>
                    <h3>{t('mapGenPage.hiw4Title', 'Keep everything')}</h3>
                    <p>{t('mapGenPage.hiw4Desc', 'Your maps stay on your profile, your Strava, and your camera roll.')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Free to create */}
        <div className="section-darker">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('mapGenPage.pricingTitle', 'Free to create')}</h2>
                <p className="section-description">
                  {t('mapGenPage.pricingDesc', 'Fixing your indoor maps is free forever. Art maps run on diamonds, and you earn them free from challenges, referrals, and sharing your maps.')}
                </p>
              </div>

              <div className="map-gen-stats">
                <div className="map-gen-stat">
                  <span className="map-gen-stat-value">$0</span>
                  <span className="map-gen-stat-label">{t('mapGenPage.statFree', 'Indoor map fix, always free')}</span>
                </div>
                <div className="map-gen-stat">
                  <span className="map-gen-stat-value">40+</span>
                  <span className="map-gen-stat-label">{t('mapGenPage.statStyles', 'Styles in production, growing monthly')}</span>
                </div>
                <div className="map-gen-stat">
                  <span className="map-gen-stat-value">30k+</span>
                  <span className="map-gen-stat-label">{t('mapGenPage.statMaps', 'Maps generated and counting')}</span>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="section-inner challenge-cta-wrapper">
          <div className="challenge-cta-banner">
            <button
              type="button"
              className="challenge-cta-button"
              onClick={handleStravaCta}
            >
              {t('mapGenPage.finalCta', 'Start the art')}
              <IconChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Community */}
        <div className="section-dark">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('mapGenPage.communityTitle', 'The community')}</h2>
                <p className="section-description">
                  {t('mapGenPage.communityDesc', 'Vote on the next style, share your maps, and adventure with the community.')}
                </p>
              </div>

              <div className="community-cards">
                <a
                  href="https://discord.gg/neXEXTcQpW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card discord-card"
                  onClick={() => posthog?.capture('map_gen_community_link_clicked', { platform: 'discord' })}
                >
                  <div className="community-card-icon discord-icon">
                    <IconBrandDiscord size={24} />
                  </div>
                  <div className="community-card-text">
                    <h3>{t('mapGenPage.discordTitle', 'Vote on Discord')}</h3>
                    <p>{t('mapGenPage.discordDesc', 'Suggest the next style and vote with the community.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>

                <a
                  href="https://www.strava.com/clubs/mythicmaps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card strava-card"
                  onClick={() => posthog?.capture('map_gen_community_link_clicked', { platform: 'strava_club' })}
                >
                  <div className="community-card-icon strava-icon">
                    <IconBrandStrava size={24} />
                  </div>
                  <div className="community-card-text">
                    <h3>{t('mapGenPage.stravaClubTitle', 'Strava Club')}</h3>
                    <p>{t('mapGenPage.stravaClubDesc', 'Join the Mythic Maps club to connect with other adventurers.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>

                <a
                  href="https://www.reddit.com/r/mythicmapsai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card reddit-card"
                  onClick={() => posthog?.capture('map_gen_community_link_clicked', { platform: 'reddit' })}
                >
                  <div className="community-card-icon reddit-icon">
                    <IconBrandReddit size={24} />
                  </div>
                  <div className="community-card-text">
                    <h3>{t('mapGenPage.redditTitle', 'Reddit')}</h3>
                    <p>{t('mapGenPage.redditDesc', 'Share your maps, discuss styles, request new ones.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>

                <a
                  href="mailto:contact@mythicmaps.ai"
                  className="community-card email-card"
                  onClick={() => posthog?.capture('map_gen_community_link_clicked', { platform: 'email' })}
                >
                  <div className="community-card-icon email-icon">
                    <IconMail size={24} />
                  </div>
                  <div className="community-card-text">
                    <h3>{t('mapGenPage.emailTitle', 'Email us')}</h3>
                    <p>{t('mapGenPage.emailDesc', 'Questions, ideas, or feedback? Drop us a line.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="section-default">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('mapGenPage.faqTitle', 'Frequently asked questions')}</h2>
              </div>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`faq-item ${openFaq === index ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="faq-question"
                      onClick={() => {
                        const isOpening = openFaq !== index;
                        setOpenFaq(isOpening ? index : null);
                        if (isOpening) {
                          posthog?.capture('map_gen_faq_toggled', { faq_index: index });
                        }
                      }}
                    >
                      <span>{faq.q}</span>
                      {openFaq === index ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer">
                        <p>{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Footer />

      </div>
    </div>
  );
};

export default MapGenerationPage;
